import React, { forwardRef, ReactElement, ChangeEvent } from 'react';
import classNames from 'classnames';
import {
  COMBO_BOX_CLASS,
  SELECT_CLASS,
  INPUT_CLASS,
  COMBO_BOX_PRISTINE_CLASS,
  COMBO_BOX_DISABLED_CLASS,
} from './ComboBoxClasses';
import Select from '../Select';
import ComboBoxButtons from './ComboBoxButtons';
import ComboBoxOptions from './ComboBoxOptionsList';
import ScreenReaderText from './ScreenReaderText';
import useComboBox from './useComboBox';
import { ComboBoxFilterOptions } from './generateDynamicRegularExpression';

export interface ComboBoxProps {
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  className?: string;
  children: ReactElement<HTMLOptionElement> | ReactElement<HTMLOptionElement>[];
  defaultValue?: string | number;
  disabled?: boolean;
  disableFiltering?: boolean;
  filter?: string;
  filterOptions?: ComboBoxFilterOptions;
  id: string;
  labelId: string;
  name?: string;
  onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
  placeholder?: string;
  required?: boolean;
  value?: string | number;
}

/**
 * The ComboBox based on [The USWDS Combo Box component](https://designsystem.digital.gov/components/form-controls/#combo-box)
 */
const ComboBox = forwardRef<HTMLSelectElement, ComboBoxProps>(
  (
    {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      'aria-describedby': ariaDescribedBy,
      className,
      children,
      defaultValue,
      disabled,
      disableFiltering,
      filter,
      filterOptions,
      id,
      labelId,
      name,
      onChange: controlledOnChange,
      placeholder,
      required,
      value: controlledValue,
    }: ComboBoxProps,
    ref,
  ): JSX.Element => {
    const {
      showList,
      focusedIndex,
      noSelectionIndex,
      displayValue,
      pristine,
      value,
      filteredOptions,
      clearInput,
      toggleList,
      handleInputChange,
      handleInputClick,
      handleInputKeyDown,
      handleOptionClick,
      handleOptionKeyDown,
      handleOptionMouseMove,
      comboBoxRef,
      selectRef,
      inputRef,
      listRef,
      focusRef,
    } = useComboBox(ref, children, defaultValue, controlledValue, filter, filterOptions, disableFiltering);

    const listId = `${id}--list`;
    const assistiveHintID = `${id}--assistiveHint`;

    return (
      <div
        ref={comboBoxRef}
        className={classNames(className, {
          [COMBO_BOX_CLASS]: true,
          [COMBO_BOX_PRISTINE_CLASS]: pristine,
          [COMBO_BOX_DISABLED_CLASS]: disabled,
        })}
      >
        <Select
          ref={selectRef}
          id=""
          name={name}
          value={value}
          onChange={controlledOnChange || ((): void => undefined)}
          className={classNames(SELECT_CLASS, 'usa-sr-only')}
          aria-hidden
          tabIndex={-1}
        >
          {children}
        </Select>
        <input
          ref={inputRef}
          aria-owns={listId}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-describedby={[ariaDescribedBy, assistiveHintID].filter(Boolean).join(' ')}
          aria-expanded={showList}
          aria-activedescendant={
            showList && focusedIndex !== null && focusedIndex > -1 ? `${id}--option-${focusedIndex}` : ''
          }
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledby}
          aria-required={required}
          autoCapitalize="off"
          autoComplete="off"
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          id={id}
          className={INPUT_CLASS}
          type="text"
          role="combobox"
          value={displayValue}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onKeyDown={handleInputKeyDown}
        />
        <ComboBoxButtons clearInput={clearInput} toggleList={toggleList} disabled={disabled} />
        <ComboBoxOptions
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...{
            id,
            listId,
            labelId,
            options: filteredOptions,
            value,
            focusedIndex,
            noSelectionIndex,
            focusRef,
            showList,
            listRef,
            handleOptionClick,
            handleOptionKeyDown,
            handleOptionMouseMove,
          }}
        />
        <ScreenReaderText
          showList={showList}
          filteredOptionsLength={filteredOptions.length}
          assistiveHintID={assistiveHintID}
          ariaHidden
        />
      </div>
    );
  },
);

export default ComboBox;
